import { useState, useEffect } from "react";
import { getZone } from "./utils";

export function useRunData(stravaId, runId, zoneConfig) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/runs/${stravaId}/${runId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
    })
      .then(r => r.json())
      .then(data => {
        setRun(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [stravaId, runId]);

  if (!run) return { run: null, loading, streams: null };

  const raw = run.raw_json ? JSON.parse(run.raw_json) : {};
  const streams = run.streams_json ? JSON.parse(run.streams_json) : null;

  const rawTime = streams?.time?.data || [];

  // Clip all streams at moving_time to exclude post-run walking on the same route.
  // Strava's moving_time = seconds of actual movement. The time stream is
  // cumulative elapsed seconds, so any index where time > moving_time is
  // after the run ended (e.g. walking home).
  const movingTime = run.moving_time; // seconds
  const clipIdx =
    movingTime && rawTime.length
      ? (() => {
          const idx = rawTime.findIndex(t => t > movingTime);
          return idx === -1 ? rawTime.length : idx;
        })()
      : null;

  function clip(arr) {
    if (!arr?.length || clipIdx === null) return arr || [];
    return arr.slice(0, clipIdx);
  }

  // Running portion (clipped at moving_time)
  const hrData = clip(streams?.heartrate?.data);
  const velocityData = clip(streams?.velocity_smooth?.data);
  const altitudeData = clip(streams?.altitude?.data);
  const distanceData = clip(streams?.distance?.data);
  const timeData = clip(rawTime);
  const latlng = clip(streams?.latlng?.data);

  // Full data including post-run walk (for charts and map walk overlay)
  const fullHrData = streams?.heartrate?.data || [];
  const fullVelocityData = streams?.velocity_smooth?.data || [];
  const fullAltitudeData = streams?.altitude?.data || [];
  const fullDistanceData = streams?.distance?.data || [];
  const fullTimeData = rawTime;
  const fullLatlng = streams?.latlng?.data || [];

  // Whether there is a post-run portion to show
  const hasPostRun = clipIdx !== null && clipIdx < (rawTime.length || 0);
  const walkLatlng = hasPostRun ? fullLatlng.slice(clipIdx) : [];

  // Zone distribution (running portion only)
  const zoneDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  hrData.forEach(hr => {
    const z = getZone(hr, zoneConfig);
    if (z) zoneDist[z]++;
  });

  return {
    run,
    loading,
    raw,
    streams,
    hrData,
    velocityData,
    altitudeData,
    distanceData,
    timeData,
    latlng,
    fullHrData,
    fullVelocityData,
    fullAltitudeData,
    fullDistanceData,
    fullTimeData,
    fullLatlng,
    walkLatlng,
    hasPostRun,
    clipIdx,
    zoneDist,
    totalHrPoints: hrData.length,
    hasStreams: hrData.length > 0 || velocityData.length > 0,
    hasMap: !!(run.summary_polyline || raw.map?.summary_polyline),
  };
}
